/**
 * The two path cards (sheet 11 callouts 1 and 7; FR-006; U.1, U.4, ANA.13,
 * X.10; style guide §8 radio-card pattern, §7.3).
 *
 * A native radio group: each card is a `<label>` holding a visually hidden
 * `<input type="radio" name="path">`, so the arrow keys, focus and `checked`
 * come from the platform. The selected card has a 2px Sky-burst border and an
 * accent-50 header strip; the focus ring sits on the card. Path B's key rules
 * (register 2) stay visible while it is collapsed, with the admission cap and
 * the Path B byte limit from the layout document; a skeleton stands in while
 * the document loads, and register 16 when it fails, never a number.
 */
import type { ReactNode } from 'react'

import { cn } from '@/components/ui/cn'
import { Skeleton } from '@/components/ui/skeleton'
import type { RunPath } from '@/lib/api/runs'
import { PROVISIONAL_COPY, copyText } from '@/lib/provisional-copy'

export interface PathCardsProps {
  value: RunPath
  onChange: (path: RunPath) => void
  admissionCap?: number | undefined
  pathBBytes?: number | undefined
  /** The layout document failed: state register 16 instead of a skeleton that never resolves. */
  unavailable?: boolean | undefined
  /** The id of the heading that names the group. */
  labelledBy?: string | undefined
}

interface CardProps {
  path: RunPath
  overline: string
  title: string
  selected: boolean
  onChange: (path: RunPath) => void
  children?: ReactNode
}

function PathCard({ path, overline, title, selected, onChange, children }: CardProps) {
  return (
    <label
      className={cn(
        'block cursor-pointer overflow-hidden rounded-lg bg-white has-[:focus-visible]:[box-shadow:var(--focus-ring)]',
        selected ? 'border-2 border-[var(--color-accent)]' : 'border border-navy-200',
      )}
    >
      <input
        type="radio"
        name="path"
        value={path}
        checked={selected}
        onChange={() => onChange(path)}
        className="sr-only"
      />
      <div className={cn('px-4 py-3', selected && 'bg-[var(--color-accent-50)]')}>
        <p className="text-overline uppercase text-navy-600">{overline}</p>
        <p className="text-h3 text-navy-950">{title}</p>
      </div>
      {children ? <div className="px-4 pb-4 text-body-sm text-navy-700">{children}</div> : null}
    </label>
  )
}

export function PathCards({ value, onChange, admissionCap, pathBBytes, unavailable = false, labelledBy }: PathCardsProps) {
  return (
    <div role="radiogroup" aria-labelledby={labelledBy} className="grid grid-cols-2 gap-4">
      <PathCard path="generate" overline="Path A" title="Generate" selected={value === 'generate'} onChange={onChange} />
      <PathCard path="analyze" overline="Path B" title="Analyze" selected={value === 'analyze'} onChange={onChange}>
        {admissionCap !== undefined && pathBBytes !== undefined ? (
          <p>{PROVISIONAL_COPY.pathBRules.render({ admissionCap, pathBBytes })}</p>
        ) : unavailable ? (
          // Plain text, not a second role="alert": the dropzone's band already announces it with Retry.
          <p className="text-[var(--color-error-700)]">{copyText('layoutUnreachable')}</p>
        ) : (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        )}
      </PathCard>
    </div>
  )
}
